import React, { useState, useCallback, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { CircleFlagLanguage } from "react-circle-flags";
import IconProvider from "@/components/ui/IconProvider";
import EditableTextInput from "./EditableTextInput";
import ExpandableCellEditor from "./ExpandableCellEditor";
import { decodeHtml, renderHtml } from "@/lib/utils";
import { sanitizeHTML, normalizeContent } from "@/lib/utils/textFormatting";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TMSTableCellProps {
  content: string | React.ReactNode;
  type?: "text" | "badge" | "checkbox" | "status-dot" | "empty" | "header-checkbox";
  status?: string;
  title?: string;
  className?: string;
  maxWidth?: number;
  width?: number;
  isHeader?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (checked: boolean) => void;
  isEmpty?: boolean;
  languageCode?: string; // Language code for flag display (e.g., "us", "fr", "de", "nl")
  showLanguageFlag?: boolean; // Whether to show the language flag
  isRightmost?: boolean; // Whether this is the rightmost column (no right border)
  // New props for inline editing
  isEditable?: boolean;
  onCellUpdate?: (newValue: string) => void;
  onCellEditStart?: (cellRect: DOMRect, value: string, cellWidth: number, maxWidth: number) => void;
  rowData?: any; // Row data for context
  columnKey?: string; // Column key for context
  noWrapper?: boolean; // New prop: when true, don't wrap in table elements
}

const TMSTableCell: React.FC<TMSTableCellProps> = ({
  content,
  type = "text",
  status,
  title,
  className = "",
  maxWidth = 220,
  width,
  isHeader = false,
  isSelected = false,
  onSelectionChange,
  isEmpty = false,
  languageCode,
  showLanguageFlag = false,
  isRightmost = false,
  // New props for inline editing
  isEditable = false,
  onCellUpdate,
  onCellEditStart,
  rowData,
  columnKey,
  noWrapper,
}) => {
  const { t } = usePlatformTranslation();
  // Local state for visual feedback only (actual editing managed by parent)
  const [isBeingEdited, setIsBeingEdited] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  const thRef = useRef<HTMLTableHeaderCellElement>(null);
  const tdRef = useRef<HTMLTableDataCellElement>(null);

  // Helper function to get CSS ellipsis styles for text over 2 lines
  const getEllipsisStyle = () => ({
    display: "-webkit-box",
    WebkitLineClamp: 2, // Show max 2 lines
    WebkitBoxOrient: "vertical" as const,
    overflow: "hidden",
    lineHeight: "1.4em",
    maxHeight: "calc(1.4em * 2)", // 2 lines with line height
    wordWrap: "break-word" as const,
    whiteSpace: "normal" as const,
  });


  // Helper function to check if text contains styling elements
  const hasStylingElements = (text: string): boolean => {
    if (!text || typeof text !== 'string') return false;
    // Check for HTML tags
    const htmlTagRegex = /<[^>]*>/;
    if (htmlTagRegex.test(text)) return true;
    
    // Check for markdown elements
    const markdownRegex = /[*_`~# [\]()<>|]/;
    if (markdownRegex.test(text)) return true;
    
    // Check for common formatting patterns
    const formattingRegex = /(\*\*|__|\*\*|__|`{1,3}|~~|#{1,6}|\[.*?\]|\(.*?\)|>|\|)/;
    if (formattingRegex.test(text)) return true;
    
    // Check for URLs
    const urlRegex = /https?:\/\/[^\s]+/;
    if (urlRegex.test(text)) return true;
    
    return false;
  };

  // Helper function to process text for display (with styling)
  const processTextForDisplay = (text: string): React.ReactNode => {
    if (!text || typeof text !== 'string') return text;
    
    // If text contains HTML, process it safely
    if (hasStylingElements(text)) {
      try {
        // Decode HTML entities first
        const decodedText = decodeHtml(text);
        // Sanitize and normalize the HTML
        const sanitizedText = sanitizeHTML(decodedText);
        const normalizedText = normalizeContent(sanitizedText);
        
        // Return the processed HTML
        return <span dangerouslySetInnerHTML={{ __html: normalizedText }} />;
      } catch (error) {
        console.error('Error processing HTML text:', error);
        // Fallback to plain text if processing fails
        return text;
      }
    }
    
    // For plain text, just return as is
    return text;
  };

  const baseClasses = `px-3 py-3 ${isRightmost ? 'border-b' : 'border-r border-b'} border-border h-20 overflow-hidden`;
  const headerClasses = isHeader
    ? `text-left text-xs font-medium text-muted-foreground h-20 uppercase tracking-wider bg-muted ${isRightmost ? 'border-b' : 'border-r border-b'} border-border`
    : "text-sm text-foreground h-20 bg-card";

  const cellClasses = isEmpty ? "bg-muted/50" : "";

  // Use width if provided, otherwise use maxWidth
  const cellWidth = width || maxWidth;
  const cellStyle = {
    width: `${cellWidth}px`,
    maxWidth: `${maxWidth}px`,
    minWidth: `${Math.min(cellWidth, maxWidth)}px`,
    height: "80px", // h-20 equivalent (5rem = 80px)
    minHeight: "80px", // Ensure minimum height is maintained
    overflow: "hidden" as const,
    wordWrap: "break-word" as const,
    whiteSpace: "normal" as const, // Allow text to wrap
    boxSizing: "border-box" as const,
  };

  // Handle editing start - use useCallback to prevent unnecessary re-renders
  const handleEditClick = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isEditable && onCellEditStart) {
      // Get the cell's position and dimensions from the appropriate ref
      const currentElement = divRef.current || thRef.current || tdRef.current;
      if (currentElement) {
        const rect = currentElement.getBoundingClientRect();
        
        // Get the raw value from rowData if available, otherwise fall back to content
        let initialValue = "";
        if (rowData && columnKey) {
          // Handle nested properties (e.g., "translations.en.translated_text")
          if (columnKey.includes('.')) {
            const pathParts = columnKey.split('.');
            let value = rowData;
            for (const part of pathParts) {
              value = value?.[part];
              if (value === undefined || value === null) break;
            }
            initialValue = typeof value === "string" ? value : "";
          } else {
            // Handle direct properties
            initialValue = typeof rowData[columnKey] === "string" ? rowData[columnKey] : "";
          }
        } else {
          // Fallback to content if rowData/columnKey not available
          initialValue = typeof content === "string" ? content : "";
        }
        
        // Call the parent's edit start handler
        onCellEditStart(rect, initialValue, width || maxWidth, maxWidth);
      }
    }
  }, [isEditable, content, onCellEditStart, width, maxWidth, rowData, columnKey]);

  // Clean up function when component unmounts
  useEffect(() => {
    return () => {
      setIsBeingEdited(false);
    };
  }, []);

  const renderContent = () => {
    switch (type) {
      case "checkbox":
        if (isEmpty) {
          return <div className="h-full flex items-center justify-center  " />;
        }
        return (
          <div className="h-full flex items-center justify-center">
            <Checkbox
              checked={isSelected}
              onCheckedChange={onSelectionChange}
            />
          </div>
        );

      case "header-checkbox":
        return (
          <div className="h-full flex items-center justify-center">
            <Checkbox
              checked={isSelected}
              onCheckedChange={onSelectionChange}
            />
          </div>
        );

      case "badge":
        if (!content) return null;
        return (
          <div className="h-full flex items-center">
            <Badge
              variant={
                status === "approved"
                  ? "default"
                  : status === "review"
                  ? "secondary"
                  : status === "draft"
                  ? "outline"
                  : "outline"
              }
            >
              {content}
            </Badge>
          </div>
        );

      case "status-dot":
        if (isEmpty || !content) {
          return (
            <div className="h-full flex items-center">
              <span className="text-muted-foreground text-sm">-</span>
            </div>
          );
        }
        return (
          <div className="h-full flex items-center gap-2 relative">
            <div
              className={`w-3 h-3 rounded-full flex-shrink-0 mt-1 ${
                status === "approved"
                  ? "bg-green-300 dark:bg-green-600"
                  : status === "translated"
                  ? "bg-yellow-200 dark:bg-yellow-500"
                  : status === "blocked"
                  ? "bg-red-200 dark:bg-red-500"
                  : status === "update"
                  ? "bg-orange-200 dark:bg-orange-500"
                  : "bg-muted"
              }`}
            />
            {showLanguageFlag && languageCode && (
              <CircleFlagLanguage
                languageCode={languageCode}
                className="w-6 h-6 flex-shrink-0"
              />
            )}
            <div
              className="flex-1 min-w-0"
              style={getEllipsisStyle()}
            >
              {processTextForDisplay(content as string)}
            </div>
            
            {/* Styling elements indicator icon */}
            {typeof content === 'string' && hasStylingElements(content) && (
              <div 
                className="absolute top-1 right-1 cursor-pointer"
                onClick={() => alert("open model")}
              >
                <IconProvider 
                  iconstring="fa-square-arrow-up-right" 
                  size={12} 
                  className="opacity-70 text-destructive"
                />
              </div>
            )}
          </div>
        );

      case "empty":
        return (
          <div className="h-full flex items-center">
            <span className="text-muted-foreground text-sm">-</span>
          </div>
        );

      case "text":
      default:
        // For editable text cells when being edited (visual feedback only)
        // The actual editor is rendered as an overlay at the widget level
        if (isBeingEdited && isEditable) {
          return (
            <div className="h-full flex items-center gap-2 relative opacity-50">
              {showLanguageFlag && languageCode && (
                <CircleFlagLanguage
                  languageCode={languageCode}
                  className="w-6 h-6 flex-shrink-0"
                />
              )}
              <div
                className="flex-1"
                style={{
                  ...getEllipsisStyle(),
                  maxWidth: `${maxWidth - 40}px`,
                }}
              >
                {processTextForDisplay(content as string)}
              </div>
              
              {/* Small editing indicator */}
              <div className="absolute top-1 right-1">
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
              </div>
            </div>
          );
        }

        // For editable text cells, show empty state as clickable placeholder
        if (isEmpty && isEditable) {
          return (
            <div className="h-full flex items-center gap-2 relative">
              {showLanguageFlag && languageCode && (
                <CircleFlagLanguage
                  languageCode={languageCode}
                  className="w-6 h-6 flex-shrink-0"
                />
              )}
              <div
                className="flex-1 cursor-pointer hover:bg-muted px-2 py-1 rounded border border-dashed border-input text-muted-foreground text-sm transition-colors duration-200 hover:border-input hover:text-foreground"
                title={t('platform.cards.widgets.click_to_add_translation_title_2')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(e);
                }}
              >
                Click to add translation
              </div>
            </div>
          );
        }
        
        // For non-editable empty cells, show dash
        if (isEmpty) {
          return (
            <div className="h-full flex items-center">
              <span className="text-muted-foreground text-sm">-</span>
            </div>
          );
        }

        // Normal text display with click-to-edit
        return (
          <div className="h-full flex items-center gap-2 relative">
            {showLanguageFlag && languageCode && (
              <CircleFlagLanguage
                languageCode={languageCode}
                className="w-6 h-6 flex-shrink-0"
              />
            )}
            <div
              className={`flex-1 flex items-center ${isEditable ? 'cursor-pointer hover:bg-muted px-1 py-0.5 border border-transparent hover:border-input transition-all duration-200' : ''}`}
              style={{
                ...getEllipsisStyle(),
                maxWidth: `${maxWidth - 40}px`,
              }}
              title={isEditable ? "Click to edit" : (typeof content === 'string' ? content : title)}
              onClick={isEditable ? (e) => {
                e.stopPropagation();
                handleEditClick(e);
              } : undefined}
            >
              {processTextForDisplay(content as string)}
            </div>
          </div>
        );
    }
  };

  if (noWrapper) {
    // Return just the content without table element wrapper
    return (
      <div
        ref={divRef}
        className={`${baseClasses} ${headerClasses} ${cellClasses} ${className}`}
        style={cellStyle}
      >
        {renderContent()}
      </div>
    );
  }

  if (isHeader) {
    return (
      <th
        ref={thRef}
        className={`${baseClasses} ${headerClasses} ${className}`}
        style={cellStyle}
      >
        {renderContent()}
      </th>
    );
  }

  return (
    <td
      ref={tdRef}
      className={`${baseClasses} ${headerClasses} ${cellClasses} ${className}`}
      style={cellStyle}
    >
      {renderContent()}
    </td>
  );
};

export default TMSTableCell;
